import { Box, Button, Divider, Typography } from "@mui/material";
import { SectionHeader } from "apps/web/src/componentLibrary/SectionHeader";
import { GoalConfigurationTemplateTableItemFragment, TeamWithAthletesFragment } from "apps/web/src/types";
import { UseFormReturn } from "react-hook-form";
import { GoalAssignmentCalendarDateAssignment } from "../../Athlete/GoalTracker/GoalConfiguration/Form/GoalConfigurationFormFrequency";
import { GoalConfigurationRemindersForm } from "../../Athlete/GoalTracker/GoalConfiguration/Form/GoalConfigurationReminders";
import { GoalConfigurationFormType } from "../../Athlete/GoalTracker/GoalConfiguration/GoalConfigurationSchema";
import { MealPlanCalendarContextProvider } from "../../Athlete/MealPlans/contexts/Calendar";
import AdvancedSelectionDialog from "../../Tags/Dialogs/Mutation/AdvancedSelection/AdvancedSelectionDialog";
import ShareWithInfo from "../../Tags/Dialogs/Mutation/AdvancedSelection/ShareWithInfo";
import { buildInitialCreateAdvancedSelectionStateFromTeams } from "../../Tags/reducers/advancedSelectionReducers";
import { AssignGoalAdvancedSelectionAction, AssignGoalAdvancedSelectionState } from "../useAdvancedSelection";
import TeamMultiSelect from "./TeamMultiSelect";

interface BoldTextProps {
  text: string;
}

const BoldText = ({ text }: BoldTextProps) => {
  return (
    <Typography variant="body2Medium" color="mediumEmphasisTextLight">
      {text}
    </Typography>
  );
};

interface AssignGoalConfigFromTemplateDialogContentProps {
  goalConfigTemplate: GoalConfigurationTemplateTableItemFragment;
  form: UseFormReturn<GoalConfigurationFormType>;
  advancedSelectState: AssignGoalAdvancedSelectionState;
  advancedSelectDispatch: React.Dispatch<AssignGoalAdvancedSelectionAction>;
  allTeams: readonly TeamWithAthletesFragment[];
  assignToError: boolean;
  setAssignToError: (value: boolean) => void;
  advancedSelectionOpen: boolean;
  setAdvancedSelectionOpen: (value: boolean) => void;
}

const AssignGoalConfigFromTemplateDialogContent = ({
  goalConfigTemplate,
  form,
  advancedSelectState,
  advancedSelectDispatch,
  allTeams,
  assignToError,
  setAssignToError,
  advancedSelectionOpen,
  setAdvancedSelectionOpen,
}: AssignGoalConfigFromTemplateDialogContentProps) => {
  return (
    <>
      <SectionHeader header="Selected Goal" subcopy="Below are the details of the selected goal" />
      <Box
        sx={theme => ({
          display: "flex",
          borderRadius: 1,
          border: 1,
          p: 2,
          flexDirection: "column",
          borderColor: theme.palette.greyscaleLight[200],
          bgcolor: theme.palette.greyscaleLight[50],
        })}
      >
        <Typography variant="body1Medium" sx={{ mb: 0.5 }}>
          {`${goalConfigTemplate.category.defaultEmoji} ${goalConfigTemplate.name}`}
        </Typography>
        <Typography variant="body2" color="mediumEmphasisTextLight">
          <BoldText text={"Type:"} /> {goalConfigTemplate.category.name} |
          <BoldText text={" Completion Criteria: "} />
          {goalConfigTemplate.target ? `Update progress | ` : "Mark as Complete"}
          {goalConfigTemplate.target && (
            <>
              <BoldText text={" Quantity:"} /> {goalConfigTemplate.target.value} |
              <BoldText text={" Unit:"} /> {goalConfigTemplate.target.unit}
            </>
          )}
        </Typography>
      </Box>

      <SectionHeader header="Frequency" subcopy="Select the frequency of the goal" />
      <Box sx={{ display: "flex", gap: 2, pb: 2 }}>
        <MealPlanCalendarContextProvider>
          <GoalAssignmentCalendarDateAssignment form={form} mode="create" />
        </MealPlanCalendarContextProvider>
      </Box>

      <Divider />

      <GoalConfigurationRemindersForm form={form} />

      <SectionHeader header="Assign To" subcopy="Select the specific teams and/or athletes to assign the goal(s) to" />

      <Box sx={{ display: "flex", alignItems: "baseline" }}>
        {advancedSelectState.__typename === "Teams" && (
          <TeamMultiSelect
            allTeams={allTeams}
            selectedTeams={advancedSelectState.teams}
            onChange={teams => {
              advancedSelectDispatch({
                type: "ChangeTeamsAction",
                payload: {
                  teams,
                },
              });
              setAssignToError(false);
            }}
            error={assignToError}
          />
        )}

        {advancedSelectState.__typename === "Tags" && (
          <ShareWithInfo
            state={advancedSelectState.advancedSelectionState}
            onClear={() => {
              advancedSelectDispatch({ type: "ClearAdvancedSelectionAction" });
              setAssignToError(false);
            }}
            maxChips={4}
            containerSx={{ minWidth: "376px", lineHeight: "14px" }}
            showAdvancedSelectionClear={true}
            showWrapper
          />
        )}

        <Button sx={{ height: "36px", minWidth: "200px", ml: 2 }} variant="outlined" onClick={() => setAdvancedSelectionOpen(true)}>
          {advancedSelectState.__typename === "Teams" ? "Advanced Selection" : "Edit Advanced Selection"}
        </Button>
      </Box>

      <AdvancedSelectionDialog
        title="Assign Goal Template"
        open={advancedSelectionOpen}
        onClose={() => setAdvancedSelectionOpen(false)}
        onSave={advancedSelectionState => {
          advancedSelectDispatch({
            type: "ChangeAdvancedSelectionAction",
            payload: { advancedSelectionState },
          });
          setAdvancedSelectionOpen(false);
          setAssignToError(false);
        }}
        initialState={
          advancedSelectState.__typename === "Teams"
            ? buildInitialCreateAdvancedSelectionStateFromTeams(advancedSelectState.teams)
            : advancedSelectState.advancedSelectionState
        }
      />
    </>
  );
};

export default AssignGoalConfigFromTemplateDialogContent;
